import React, { useState } from "react";
import {
  Card,
  Tabs,
  InputNumber,
  Row,
  Col,
  Space,
  Switch,
  Slider,
  Button,
  Typography,
  message,
} from "antd";
import { HistoryOutlined } from "@ant-design/icons";
import Axios from "../Axios";
import { useFutureData } from "../Context/futureContext";

const { Text } = Typography;

export default function PlaceOrder({ quantity }) {
  const { getFutureTicker } = useFutureData();
  const [mode, setMode] = useState("limit");
  const [price, setPrice] = useState(null);
  const [amount, setAmount] = useState(null);
  const [useSLTP, setUseSLTP] = useState(true);
  const [sl, setSl] = useState(null);
  const [tp, setTp] = useState(null);
  const [load, setLoad] = useState({ buy: false, sell: false });

  const normalizeFutureSymbol = (pair = "") =>
    String(pair).replace(/[_/]/g, "").toUpperCase();

  const formatFuturePayloadSymbol = (pair = "") => {
    const normalized = normalizeFutureSymbol(pair);
    const knownQuotes = ["USDT", "USDC", "USD", "BTC", "ETH"];
    const quote = knownQuotes.find((item) => normalized.endsWith(item));

    if (!quote) return normalized;

    return `${normalized.slice(0, -quote.length)}_${quote}`;
  };

  const getFutureReferencePrice = (pair = "") => {
    const ticker = getFutureTicker(pair);

    return Number(
      ticker?.midprice || ticker?.markPrice || ticker?.ask || ticker?.bid || 0,
    );
  };

  const placeFutureTrade = async (SIDE) => {
    try {
      const side = String(SIDE || "").toLowerCase();
      const qty = Number(amount ?? quantity);

      if (!Number.isFinite(qty) || qty <= 0) {
        return message.error("Please enter a valid Quantity");
      }
      setLoad((p) => ({ ...p, [side]: true }));

      const futurePair = localStorage.getItem("currencyPair") || "";
      const entryPrice =
        mode === "limit" ? Number(price) : getFutureReferencePrice(futurePair);
      if (!Number.isFinite(entryPrice) || entryPrice <= 0) {
        return message.error(
          mode === "limit"
            ? "Please enter a valid order price"
            : "Live market price is not ready yet",
        );
      }

      const payload = {
        quantity: qty,
        side,
        price: entryPrice,
        symbol: formatFuturePayloadSymbol(futurePair),
        type: mode,
        marginMode: "cross",
      };

      if (useSLTP) {
        const stopLossPrice = Number(sl);
        const takeProfitPrice = Number(tp);
        const isBuy = side === "buy";

        if (Number.isFinite(takeProfitPrice) && takeProfitPrice > 0) {
          if (isBuy && takeProfitPrice <= entryPrice) {
            return message.error("For BUY, TP must be above entry price.");
          }
          if (!isBuy && takeProfitPrice >= entryPrice) {
            return message.error("For SELL, TP must be below entry price.");
          }

          payload.takeProfit = takeProfitPrice;
          payload.tradeProfit = Number(
            (Math.abs(takeProfitPrice - entryPrice) * qty).toFixed(8),
          );
        }

        if (Number.isFinite(stopLossPrice) && stopLossPrice > 0) {
          if (isBuy && stopLossPrice >= entryPrice) {
            return message.error("For BUY, SL must be below entry price.");
          }
          if (!isBuy && stopLossPrice <= entryPrice) {
            return message.error("For SELL, SL must be above entry price.");
          }

          payload.stopLoss = stopLossPrice;
          payload.tradeLoss = Number(
            (Math.abs(entryPrice - stopLossPrice) * qty).toFixed(8),
          );
        }
      }

      const { data } = await Axios.post("/ownTrade/openPosition", payload, {
        headers: { Authorization: localStorage.getItem("nzanzi") },
      });
      if (data?.success) {
        message.success(data?.message || "Trade placed");
        setPrice(null);
        setAmount(null);
        setSl(null);
        setTp(null);
      } else {
        message.error(data?.message || "Failed to place trade");
      }
    } catch (e) {
      console.error(e, "ERROR");
      message.error(e?.response?.data?.message || "Failed to place trade");
    } finally {
      setLoad({ buy: false, sell: false });
    }
  };

  return (
    <Card
      style={{
        width: "100%",
        borderRadius: 12,
        background: "var(--bg-mn)",
        border: "none",
        height: "100%",
      }}
      bodyStyle={{
        padding: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header title buttons */}
      {/* <div className="header-row">
        <div className="title-book" style={{ color: "var(--fnt)" }}>PlaceOrder</div>

      </div> */}
      {/* Tabs + SL/TP */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col flex="auto">
          <Tabs
            activeKey={mode}
            onChange={setMode}
            className="limi-mt"
            // type="card"
            size="small"
            style={{ marginBottom: 0 }}
            items={[
              { label: "Limit", key: "limit" },
              { label: "Market", key: "market" },
            ]}
          />
        </Col>
      </Row>
      <div
        className="placeorder-wrapper"
        style={{ overflowY: "scroll", overflowX: "hidden" }}
      >
        {/* LIMIT MODE */}
        {mode === "limit" && (
          <>
            {/* Order Price */}
            <div style={{ marginBottom: 0, padding: 8 }}>
              <div className="display-5">
                <Text type="secondary" style={{ color: "#7c808a" }}>
                  Order Price
                  {/* <span style={{ color: "#7c808a" }}>(USDT)</span> */}
                </Text>
                <Col>
                  <Space>
                    <Text
                      type="secondary"
                      style={{ color: "#7c808a", fontSize: 11 }}
                    >
                      TP / SL
                    </Text>
                    <Switch
                      checked={useSLTP}
                      onChange={setUseSLTP}
                      size="small"
                    />
                  </Space>
                </Col>
              </div>

              <InputNumber
                suffix={"USDT"}
                value={price}
                onChange={setPrice}
                className="place-inp"
                style={{
                  width: "100%",
                  background: "var(--bg-mn)",
                  marginTop: 5,
                  borderColor: "rgb(124 128 138 / 13%)",
                }}
                placeholder="Enter price"
                min={0}
              />
            </div>

            {/* Size */}
            <div style={{ marginBottom: 0, padding: 8 }}>
              <Text type="secondary" style={{ color: "#7c808a" }}>
                Size
                {/* <span style={{ color: "#fff" }}>(USDT)</span> */}
              </Text>
              <InputNumber
                value={amount}
                className="place-inp"
                onChange={setAmount}
                style={{
                  width: "100%",
                  background: "var(--bg-mn)",
                  color: "#7c808a",
                  marginTop: 5,
                  borderColor: "rgb(124 128 138 / 13%)",
                }}
                placeholder="Enter size"
                min={0}
                suffix={"COMP"}
              />
            </div>

            {/* Slider */}
            <div style={{ padding: "0px 15px" }}>
              <Slider
                tooltip={{ formatter: (v) => `${v}%` }}
                marks={{
                  0: "0%",
                  25: "25%",
                  50: "50%",
                  75: "75%",
                  100: "100%",
                }}
                onChange={(v) => {
                  const balance = 1000;
                  const effectivePrice = price || 1;
                  const calculated = +(
                    (balance * (v / 100)) /
                    effectivePrice
                  ).toFixed(6);
                  setAmount(calculated);
                }}
                style={{ marginBottom: "10px" }}
              />
            </div>
          </>
        )}

        {/* MARKET MODE */}
        {mode === "market" && (
          <>
            {/* Only Size Input */}
            <div style={{ marginBottom: 12, padding: 8 }}>
              <div className="display-5">
                <Text type="secondary" style={{ color: "#7c808a" }}>
                  Size
                  {/* <span style={{ color: "#fff" }}>(USDT)</span> */}
                </Text>
                <Col>
                  <Space>
                    <Text
                      type="secondary"
                      style={{ color: "#7c808a", fontSize: 11 }}
                    >
                      TP / SL
                    </Text>
                    <Switch
                      checked={useSLTP}
                      onChange={setUseSLTP}
                      size="small"
                    />
                  </Space>
                </Col>
              </div>

              <InputNumber
                suffix={"USDT"}
                className="place-inp"
                value={amount}
                onChange={setAmount}
                style={{
                  width: "100%",
                  background: "var(--bg-mn)",
                  color: "#7c808a",
                  marginTop: 5,
                  borderColor: "rgb(124 128 138 / 13%)",
                }}
                placeholder="Enter size"
                min={0}
              />
            </div>

            {/* Slider */}
            <div style={{ padding: "5px", padding: 8 }}>
              <Slider
                tooltip={{ formatter: (v) => `${v}%` }}
                marks={{
                  0: "0%",
                  25: "25%",
                  50: "50%",
                  75: "75%",
                  100: "100%",
                }}
                onChange={(v) => {
                  const balance = 1000;
                  const calculated = +(balance * (v / 100)).toFixed(6);
                  setAmount(calculated);
                }}
              />
            </div>
          </>
        )}

        {/* SL / TP section */}
        {useSLTP && (
          <>
            <Row gutter={8} style={{ marginTop: 20, padding: 8 }}>
              <Col span={24} style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ color: "#7c808a" }}>
                  Stop Loss
                  {/* (USDT) */}
                </Text>
                <InputNumber
                  value={sl}
                  className="place-inp"
                  onChange={setSl}
                  style={{
                    width: "100%",
                    background: "var(--bg-mn)",
                    color: "var(--fnt)",
                    marginTop: 5,
                    borderColor: "rgb(124 128 138 / 13%)",
                  }}
                  placeholder="SL price"
                  min={0}
                />
              </Col>

              <Col span={24} style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ color: "#7c808a" }}>
                  Take Profit
                  {/* (USDT) */}
                </Text>
                <InputNumber
                  value={tp}
                  onChange={setTp}
                  className="place-inp"
                  style={{
                    width: "100%",
                    background: "var(--bg-mn)",
                    color: "var(--fnt)",
                    marginTop: 5,
                    borderColor: "rgb(124 128 138 / 13%)",
                  }}
                  placeholder="TP price"
                  min={0}
                />
              </Col>
            </Row>
          </>
        )}
      </div>

      {/* Long / Short buttons */}
      <Row gutter={[10, 10]} style={{ marginTop: 15, padding: 8 }}>
        <Col lg={12} sm={24} xs={24}>
          <Button
            type="primary"
            block
            size="large"
            style={{ background: "#16C784", borderColor: "#16C784" }}
            onClick={() => placeFutureTrade("buy")}
            disabled={load.buy}
          >
            {load.buy ? "Placing Trade..." : "Open Long"}
          </Button>
        </Col>

        <Col lg={12} sm={24} xs={24}>
          <Button
            danger
            block
            size="large"
            style={{
              background: "#EA3943",
              borderColor: "#EA3943",
              color: "#ffffffff",
              width: "100%",
            }}
            onClick={() => placeFutureTrade("sell")}
            disabled={load.sell}
          >
            {load.sell ? "Placing Trade..." : "Open Short"}
          </Button>
        </Col>
      </Row>

      {/* Header title buttons */}
      <div className="header-row" style={{ padding: 8 }}>
        <div
          className="title-book display-1"
          style={{ color: "var(--fnt)", marginTop: "15px" }}
        >
          <HistoryOutlined /> Asset Overview{" "}
        </div>
      </div>
      {/* <div className="header-row" style={{ flexDirection: "column", flex: "1", justifyContent: "center" }}>
        <div className="title-book" style={{ color: "var(--fnt)", marginTop: "15px" }}>No Data Found</div>
      </div> */}
      <div className="asset-scrl" style={{ padding: 8 }}>
        <div className="display-5">
          <div
            className="title-book"
            style={{ color: "#7c808a", marginTop: "15px" }}
          >
            USDT
          </div>
          <div
            className="title-book"
            style={{ color: "#7c808a", marginTop: "15px" }}
          >
            600 USDT
          </div>
        </div>
        <div className="display-5">
          <div
            className="title-book"
            style={{ color: "#7c808a", marginTop: "15px" }}
          >
            ETH
          </div>
          <div
            className="title-book"
            style={{ color: "#7c808a", marginTop: "15px" }}
          >
            600 ETH
          </div>
        </div>
        <div className="display-5">
          <div
            className="title-book"
            style={{ color: "#7c808a", marginTop: "15px" }}
          >
            Initial Margin
          </div>
          <div
            className="title-book"
            style={{ color: "#7c808a", marginTop: "15px" }}
          >
            0.00 %
          </div>
        </div>
        <div className="display-5">
          <div
            className="title-book"
            style={{ color: "#7c808a", marginTop: "15px" }}
          >
            Maintenance Margin
          </div>
          <div
            className="title-book"
            style={{ color: "#7c808a", marginTop: "15px" }}
          >
            0.00 %
          </div>
        </div>
        <div className="display-5">
          <div
            className="title-book"
            style={{ color: "#7c808a", marginTop: "15px" }}
          >
            Margin Balance
          </div>
          <div
            className="title-book"
            style={{ color: "#7c808a", marginTop: "15px" }}
          >
            0.00 USDT
          </div>
        </div>
        <div className="display-5">
          <div
            className="title-book"
            style={{ color: "#7c808a", marginTop: "15px" }}
          >
            Available Balance
          </div>
          <div
            className="title-book"
            style={{ color: "#7c808a", marginTop: "15px" }}
          >
            0.00 USDT
          </div>
        </div>
      </div>
    </Card>
  );
}
